import { Box } from '@mui/system'
import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { CompactFieldRow } from '../../../form/compactFieldRow'
import { FormTextField } from '../../../form/textfield'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { FormSwitch } from '../../../form/switch'
import { Button, Typography } from '@mui/material'
import LockResetIcon from '@mui/icons-material/LockReset'
import { useDialog } from '@hooks/useDialog'
import AuthService from '@services/users/auth.service'
import useAPIFetch from '@hooks/useAPIFetch'
import { useSnackbar } from 'notistack'
import { useMeta } from '@contexts/Meta'
import { AuthContext } from '@contexts/Auth'
import { PermissionCodeEnum } from '@models/permission'
import { hasPermission } from '@utils/permission_helper'

const FormBox = styled(Box)(() => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
}))

export default function UserInfoForm({
	userID,
	disableIdentityFields = false,
}: {
	userID?: string
	disableIdentityFields?: boolean
}) {
	const { t } = useTranslation()
	const [, { setMeta }] = useMeta()
	const { control } = useFormContext()
	const { enqueueSnackbar } = useSnackbar()
	const { setRequest, isLoading } = useAPIFetch()
	const { state: authState } = useContext(AuthContext)

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	const [CustomizatedDialog, controlDialog] = useDialog({
		title: t('Reset User Password'),
		children: (
			<>
				<Typography>{t('You are going to reset password of this user')}</Typography>
			</>
		),
		closeButton: true,
		actionCloseButton: true,
		actionConfirmButton: true,
		actionOnConfirm: () => {
			setRequest({
				callback: async (cancelToken: any) => {
					if (userID) {
						await AuthService.ResetPassword(userID)
							.then(() => {
								enqueueSnackbar(t('Email of reset password has sent to the user'), {
									variant: 'success',
								})
								return
							})
							.catch((error) => {
								enqueueSnackbar(error.detail, {
									variant: 'error',
								})
							})
					}
				},
			})
		},
	})

	return (
		<>
			<FormBox>
				<CompactFieldRow
						firstchildren={
							<FormTextField
								name="first_name"
								control={control}
								label={t('First Name')}
								uneditable={disableIdentityFields}
								mandatory={true}
							/>
						}
						secondchildren={
							<FormTextField
								name="last_name"
								control={control}
								label={t('Last Name')}
								uneditable={disableIdentityFields}
								mandatory={true}
							/>
						}
				/>
				<CompactFieldRow
					firstchildren={
						<FormTextField name="company" control={control} label={t('Organisation')} />
					}
					secondchildren={<FormTextField name="position" control={control} label={t('Dept/Team')} />}
				/>
				<CompactFieldRow
					firstchildren={
						<FormTextField
							name="phone"
							control={control}
							label={t('Phone')}
							uneditable={disableIdentityFields}
						/>
					}
						secondchildren={
							<FormTextField
								name="email"
								control={control}
								label={t('E-mail')}
								uneditable={disableIdentityFields}
								mandatory={true}
							/>
						}
				/>
				<CompactFieldRow
					firstchildren={<FormSwitch name="active" control={control} label={t('Active')} />}
					secondchildren={
						<Box
							sx={{
								flexDirection: 'row',
								display: 'flex',
								width: '100%',
								alignItems: 'flex-end',
								height: '40px',
								marginRight: '30px',
								marginBottom: '10px',
							}}
						>
							{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserResetPassword]) && [
								<Box sx={{ display: 'flex', minWidth: '125px' }}>
									<Typography variant="h4" color="info">
										Password :{' '}
									</Typography>
								</Box>,

								<Button
									variant="contained"
									color="defaultButton"
									onClick={controlDialog}
									disabled={isLoading}
								>
									<LockResetIcon />
									{t('Reset User Password')}
								</Button>,
							]}
						</Box>
					}
				/>
			</FormBox>
			<CustomizatedDialog />
		</>
	)
}
